import computeScrollIntoView from 'compute-scroll-into-view';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import lowerCase from 'lodash/lowerCase';
import { v4 as uuid } from 'uuid';

import {
    getDeviceProps,
    getHistoricalMetadata,
    getSessionProperties,
    mapEventProperties,
} from '@/common/event-property-filter/utils';
import { AUTOMATIC_GROUP_NAMES } from '@/constants/utm-parameters';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';
import { getWhitelistedGroups } from '@/metadata/utils';
import { getPipelineProps } from '@/segments/segment';
import { FilterType } from '@/types/analytics/analytics.types';
import { getAppIconType } from '@/utils/apps';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { FRUSTRATION_TYPES } from '@/session-replay/helpers/common.util.js';
import {
    MOBILE_VERSION_RULES_LIST,
    buildRuleTypes,
    getGuideFriendlyRules,
    getUsedTagRules,
} from '@/utils/segments.js';
import { NEW_USAGE_RULES_LIST } from '@pendo/middleware';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';

import { i18n_t as $t } from '@/utils/i18n';

export const EVENT_PROPERTY_VALUE_CHAR_LIMIT = 1000;

export function removeSpecialChars (string) {
    return typeof string === 'string' ? string.replace('\u2029', '') : string;
}

export function compileSegmentPipeline (segment) {
    return getPipelineProps(segment);
}

export function createEmptyRule () {
    return {
        key: uuid(),
    };
}

export function createEmptyRuleset () {
    return {
        ...createEmptyRule(),
        or: [createEmptyRule()],
    };
}

export function getFormattedSessionPropertyData (schema) {
    const { group, kind, field, DisplayName, ElementType } = schema;
    const isAutomaticGroup = AUTOMATIC_GROUP_NAMES.includes(schema.DisplayName);
    const iconType = isAutomaticGroup ? 'session-properties' : 'utm';
    const strokeWidth = isAutomaticGroup ? 1 : 0;

    return {
        group,
        kind: 'visitor',
        field: `properties.__utm__.${field}`,
        id: 'utm',
        type: `${kind}.${field}`,
        name: DisplayName,
        schema: 'aggregation',
        elementType: ElementType,
        icon: { type: iconType, strokeWidth },
    };
}

export function formatMetadataForRules (metadata) {
    const groups = metadata.map((schema) => schema.group);
    const whitelistOptions = { editableOnly: false, groupsArray: true };
    const whitelistedGroups = getWhitelistedGroups(groups, whitelistOptions);

    const rules = metadata
        .filter((schema) => whitelistedGroups.includes(schema.group))
        .map((schema) => {
            const { group, kind, field, DisplayName, Type, ElementType } = schema;

            let name = DisplayName || field;

            if (field.includes('firstvisit') && group === 'auto') {
                if (kind === 'visitor') {
                    name = $t('segments.ruleTypes.First visit by visitor');
                } else if (kind === 'account') {
                    name = $t('segments.ruleTypes.First visit by account');
                } else if (kind === 'parentAccount') {
                    name = $t('segments.ruleTypes.First visit by parent account');
                } else {
                    name = `First visit by ${lowerCase(kind)}`;
                }
            }

            const isSessionProperty = schema.group === 'utm';
            if (isSessionProperty) {
                return getFormattedSessionPropertyData(schema);
            }

            return {
                group,
                kind,
                field: `${kind}.${group}.${field}`,
                id: `${kind}_${group}_${field}`,
                type: `${kind}.${field}`,
                name,
                schema: Type || 'string',
                untyped: !Type,
                elementType: ElementType,
            };
        })
        .concat({
            group: 'auto',
            kind: 'visitor',
            field: 'visitor.auto.idhash%100',
            id: 'visitor_sample_group',
            type: 'visitor.auto.idhash%100',
            name: $t('segments.ruleTypes.Sample Group'),
            schema: 'number',
        });

    if (activeSubHasPurchasedProductLine('adoptEndUser')) {
        return rules;
    }

    return rules
        .concat({
            group: 'pendo',
            kind: 'visitor',
            field: 'visitor.pendo.campaignemailstatus == "bounced"',
            id: 'visitor_has_bounced_from_email',
            type: 'visitor.pendo.has_bounced_from_email',
            name: $t('segments.ruleTypes.Has bounced from email'),
            schema: 'expression',
        })
        .concat({
            group: 'pendo',
            kind: 'visitor',
            field: 'visitor.pendo.campaignemailstatus == "complaint"',
            id: 'visitor_has_email_spam_complaint',
            type: 'visitor.pendo.has_email_spam_complaint',
            name: $t('segments.ruleTypes.Has email spam complaint'),
            schema: 'expression',
        })
        .concat({
            group: 'pendo',
            kind: 'visitor',
            field: 'visitor.pendo.campaignemailstatus == "unsubscribed"',
            id: 'visitor_has_unsubscribed_from_email',
            type: 'visitor.pendo.has_unsubscribed_from_email',
            name: $t('segments.ruleTypes.Has unsubscribed from email'),
            schema: 'expression',
        });
}

export function getRules (metadata, options) {
    let rules = buildRuleTypes(metadata, options.guideFriendly);

    if (options.guideFriendly) {
        rules = rules.filter((rule) => {
            if (rule.schema === 'feedbackRequest' || rule.id === 'guideExperiment') {
                return false;
            }

            return rule.schema !== 'aggregation' || ['feature', 'page', 'trackType', 'guideElement', 'emailElement'].includes(rule.id);
        });
    }

    if (!hasSubscriptionFlag('usesSubaccounts')) {
        rules = rules.filter((rule) => !rule.id.includes('parentAccount'));
    }

    return {
        rules,
    };
}

export function getResource (rule, resources) {
    const field = `${rule.id}Id`;

    return resources?.find((resource) => resource.id === rule[field]);
}

export function getResourceConfiguration (rule, resource) {
    return (
        {
            feature: get(resource, 'eventPropertyConfigurations', []),
            trackType: get(resource, 'eventPropertyNameList', []),
            page: [],
        }[rule.id] || []
    );
}

export function getEventProperties (
    rule,
    resources,
    eventProperties,
    grouped = false,
    historicalMetadata = [],
    sessionProperties = [],
) {
    const resource = getResource(rule, resources);

    if (!resource) {
        return [];
    }

    const automaticEventProperties = getDeviceProps({ appId: resource.appId });
    const sortedAutomaticEventProperties = automaticEventProperties.sort((a, b) => a.name.localeCompare(b.name));

    const historicalMetadataList = getHistoricalMetadata(historicalMetadata);
    const sortedHistoricalMetadataList = historicalMetadataList.sort((a, b) => a.name.localeCompare(b.name));

    const sessionPropsList = getSessionProperties(sessionProperties);

    const eventPropertyConfigurations = getResourceConfiguration(rule, resource);
    const customEventProperties = eventPropertyConfigurations
        .filter((configuration) => {
            if (rule.id === 'feature') {
                return configuration.isActive;
            }

            return true;
        })
        .map((configuration) => {
            const eventProperty = eventProperties.find((eventProperty) => {
                // features store their event property configuration as an array of objects,
                // track events store event properties as an array of event property names
                if (rule.id === 'feature') {
                    return eventProperty.field === configuration.name;
                }

                return eventProperty.field === configuration;
            });

            return eventProperty
                ? {
                    name: eventProperty.DisplayName,
                    filterType: FilterType.Custom,
                    eventPropertyName: eventProperty.DisplayName,
                    eventPropertyField: eventProperty.field,
                    eventPropertySchema: eventProperty.Type,
                    eventPropertyElementType: eventProperty.ElementType,
                    eventPropertyFieldKey: eventProperty.field,
                }
                : null;
        })
        .filter((property) => property && get(property, 'eventPropertySchema') !== 'time');
    const sortedCustomEventProperties = customEventProperties.sort((a, b) => a.name.localeCompare(b.name));

    return mapEventProperties({
        automaticEventProperties: sortedAutomaticEventProperties,
        customEventProperties: sortedCustomEventProperties,
        historicalMetadata: sortedHistoricalMetadataList,
        sessionProperties: sessionPropsList,
        locationEventProperties: [],
        topLevelEventProperties: [],
        pagesWithPageParams: [],
        grouped,
    });
}

export function getPolls (guide) {
    if (!guide.polls || !guide.steps) {
        return [];
    }

    return guide.polls.map((poll) => {
        let options = [];

        if (poll.idResponses && poll.numericResponses && poll.attributes.type !== 'NumberScale') {
            options = poll.numericResponses.map((value) => {
                return { value, name: poll.idResponses[value] };
            });
        } else if (poll.idResponses && poll.attributes.type !== 'NumberScale') {
            options = Object.keys(poll.idResponses).map((value) => {
                return {
                    value,
                    name: poll.idResponses[value],
                };
            });
        } else if (poll.numericResponses) {
            options = poll.numericResponses.map((value) => {
                return { value, name: `${value}` };
            });
        }

        return {
            pollId: poll.id,
            name: poll.question,
            type: get(poll, 'attributes.type', '').toLowerCase(),
            options,
        };
    });
}

export function convertRule (rule) {
    const isoDates = {};

    if (['time', 'date'].includes(rule.schema) && isNumber(rule.value)) {
        isoDates.value = moment(rule.value, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);
    }

    if (isNumber(rule.first)) {
        isoDates.first = moment(rule.first, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);
    }

    if (isNumber(rule.last)) {
        isoDates.last = moment(rule.last, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);
    }

    if (isPlainObject(rule.selectedApp) && !rule.appId) {
        rule.appId = rule.selectedApp.id;
        delete rule.selectedApp;
    }

    if (rule.schema === 'boolean' && rule.value === false && rule.operator === '!=') {
        rule.operator = '==';
    }

    return {
        ...rule,
        ...isoDates,
    };
}

export function getOptionIconType (option) {
    // support grabbing icon from id, kind, or type to ease with support of icons in appengine segment builder
    if (['feature', 'page', 'trackType', 'guide', 'guideElement', 'emailElement', 'poll'].includes(option.id)) {
        return option.id;
    }

    if (['email'].includes(option.id)) {
        return 'mail';
    }

    if (['visitor_lastuseragent'].includes(option.id)) {
        return 'smartphone';
    }

    if (['visitor', 'account', 'parentAccount'].includes(option.kind)) {
        return option.kind;
    }

    if (['resourceCenter'].includes(option.id)) {
        return option.type;
    }

    if (['ios', 'android', 'web', 'extension'].includes(option.platform)) {
        return getAppIconType(option.platform);
    }

    if (option.eventPropertyName) {
        return 'event-property';
    }

    if (['journey'].includes(option.id)) {
        return 'orchestrate';
    }
}

export function groupOptionsByKind (options) {
    const groups = {
        productUsage: $t('segmentEditorUtils.Product usage'),
        newUsage: $t('segmentEditorUtils.New usage'),
        guideInteractions: $t('segmentEditorUtils.Guide interactions'),
        sessionProperties: $t('segmentEditorUtils.Session properties'),
        orchestrate: $t('segmentEditorUtils.Orchestrate'),
        productFeedback: $t('segmentEditorUtils.Product feedback'),
        visitor: $t('segmentEditorUtils.Visitor data'),
        account: $t('segmentEditorUtils.Account data'),
        parentAccount: $t('segmentEditorUtils.Parent account data'),
        eventProperty: $t('segmentEditorUtils.Event property'),
        mobileData: $t('segmentEditorUtils.Mobile data'),
        essentialContact: $t('segmentEditorUtils.Essential contacts'),
    };
    const isPendoInternalSub = store.state.subscription?.active?.name === 'pendo-internal';
    const groupedOptions = groupBy(options, (option) => {
        if (option.eventProperty) {
            return 'eventProperty';
        }

        if (option.id === 'utm') {
            return 'sessionProperties';
        }

        if (['application', 'feature', 'page', 'trackType', 'segment'].includes(option.id)) {
            return 'productUsage';
        }

        if (NEW_USAGE_RULES_LIST.includes(option.id)) {
            return 'newUsage';
        }

        if (['guide', 'guideExperiment', 'guideElement', 'poll', 'resourceCenter'].includes(option.id)) {
            return 'guideInteractions';
        }

        if (['journey', 'email', 'emailElement'].includes(option.id)) {
            return 'orchestrate';
        }

        if (['feedbackRequest', 'idea', 'survey'].includes(option.id)) {
            return 'productFeedback';
        }

        if (MOBILE_VERSION_RULES_LIST.includes(option.id)) {
            return 'mobileData';
        }

        if (isPendoInternalSub && option.id.startsWith('visitor_essentialcontact_')) {
            return 'essentialContact';
        }

        return option.kind;
    });

    return Object.keys(groups).reduce((acc, group) => {
        const options = groupedOptions[group];
        if (!options) {
            return acc;
        }

        acc.push({
            label: groups[group],
            id: group,
            options,
        });

        return acc;
    }, []);
}

export function getOperatorOptions ({ schema, id, untyped } = {}) {
    if (MOBILE_VERSION_RULES_LIST.includes(id)) {
        return [
            { operator: '==', name: $t('segments.operatorOptions.is equal to') },
            { operator: '!=', name: $t('segments.operatorOptions.is not equal to') },
            { operator: 'startsWith', name: $t('segments.operatorOptions.starts with') },
            { operator: '!startsWith', name: $t('segments.operatorOptions.does not start with') },
            { operator: 'contains', name: $t('segments.operatorOptions.contains') },
            { operator: '!contains', name: $t('segments.operatorOptions.does not contain') },
        ];
    }

    switch (schema) {
        case 'expression':
        case 'boolean':
            return [
                { operator: '==', name: $t('segments.operatorOptions.is true'), value: true, unary: true },
                { operator: '==', name: $t('segments.operatorOptions.is false'), value: false, unary: true },
            ];
        case 'name':
        case 'os':
        case 'deviceType':
        case 'string': {
            const options = [
                { operator: '==', name: $t('segments.operatorOptions.is equal to') },
                { operator: '!=', name: $t('segments.operatorOptions.is not equal to') },
                { operator: 'contains', name: $t('segments.operatorOptions.contains') },
                { operator: '!contains', name: $t('segments.operatorOptions.does not contain') },
                { operator: 'empty', name: $t('segments.operatorOptions.is empty'), unary: true },
                { operator: '!empty', name: $t('segments.operatorOptions.is not empty'), unary: true },
            ];

            // Visitor ID, visitor string metadata, and account ID only (exclude auto/pendo metadata groups)
            const isAutoOrPendoMetadata = id !== 'visitor_auto_id'
                && (id?.startsWith('visitor_auto_') || id?.startsWith('visitor_pendo_'));
            if ((id?.startsWith('visitor_') && !isAutoOrPendoMetadata) || id === 'account_auto_id') {
                if (untyped) {
                    options.push(
                        {
                            operator: 'listContains',
                            name: $t('segments.operatorOptions.csv contains'),
                            disabled: true,
                            tooltip: $t('segments.operatorOptions.csv_requires_type'),
                        },
                        {
                            operator: '!listContains',
                            name: $t('segments.operatorOptions.csv does not contain'),
                            disabled: true,
                            tooltip: $t('segments.operatorOptions.csv_requires_type'),
                        },
                    );
                } else {
                    options.push(
                        { operator: 'listContains', name: $t('segments.operatorOptions.csv contains') },
                        { operator: '!listContains', name: $t('segments.operatorOptions.csv does not contain') },
                    );
                }
            }

            return options;
        }
        case 'integer':
        case 'float':
        case 'number':
            return [
                { operator: '==', name: $t('segments.operatorOptions.is equal to') },
                { operator: '!=', name: $t('segments.operatorOptions.is not equal to') },
                { operator: '<=', name: $t('segments.operatorOptions.less or equal to') },
                { operator: '>=', name: $t('segments.operatorOptions.greater or equal to') },
                { operator: 'empty', name: $t('segments.operatorOptions.is empty'), unary: true },
                { operator: '!empty', name: $t('segments.operatorOptions.is not empty'), unary: true },
            ];
        case 'aggregation':
            return [
                { operator: '==', name: $t('segments.operatorOptions.is equal to') },
                { operator: '!=', name: $t('segments.operatorOptions.is not equal to') },
                { operator: '<=', name: $t('segments.operatorOptions.less or equal to') },
                { operator: '>=', name: $t('segments.operatorOptions.greater or equal to') },
            ];
        case 'time':
        case 'date':
            return [
                { operator: '>=', name: $t('segments.operatorOptions.since') },
                { operator: '<=', name: $t('segments.operatorOptions.before') },
                { operator: 'withinLast', name: $t('segments.operatorOptions.within last') },
                { operator: '!withinLast', name: $t('segments.operatorOptions.not within last') },
                { operator: 'withinNext', name: $t('segments.operatorOptions.within next') },
                { operator: '!withinNext', name: $t('segments.operatorOptions.not within next') },
                { operator: 'between', name: $t('segments.operatorOptions.between') },
                { operator: 'empty', name: $t('segments.operatorOptions.is empty'), unary: true },
                { operator: '!empty', name: $t('segments.operatorOptions.is not empty'), unary: true },
            ];
        case 'list':
            return [
                { operator: 'contains', name: $t('segments.operatorOptions.contains') },
                { operator: '!contains', name: $t('segments.operatorOptions.does not contain') },
                { operator: 'empty', name: $t('segments.operatorOptions.is empty'), unary: true },
                { operator: '!empty', name: $t('segments.operatorOptions.is not empty'), unary: true },
            ];
        case 'segment':
            return [
                { operator: '==', name: $t('segments.operatorOptions.is equal to') },
                { operator: '!=', name: $t('segments.operatorOptions.is not equal to') },
            ];
    }

    return [];
}

export function getTimeOptions () {
    return {
        aggregation: [
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: 'between', name: $t('segments.timeOptions.between') },
        ],
        guide: [
            { time: 'ever', name: $t('segments.timeOptions.ever') },
            { time: '>=', name: $t('segments.timeOptions.since') },
            { time: '<=', name: $t('segments.timeOptions.before') },
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: '!withinLast', name: $t('segments.timeOptions.not within last') },
        ],
        poll: [
            { time: 'ever', name: $t('segments.timeOptions.ever') },
            { time: '>=', name: $t('segments.timeOptions.since') },
            { time: '<=', name: $t('segments.timeOptions.before') },
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: '!withinLast', name: $t('segments.timeOptions.not within last') },
            { time: 'between', name: $t('segments.timeOptions.between') },
        ],
        journey: [
            { time: 'ever', name: $t('segments.timeOptions.ever') },
            { time: '>=', name: $t('segments.timeOptions.since') },
            { time: '<=', name: $t('segments.timeOptions.before') },
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: '!withinLast', name: $t('segments.timeOptions.not within last') },
            { time: 'between', name: $t('segments.timeOptions.between') },
        ],
        email: [
            { time: 'ever', name: $t('segments.timeOptions.ever') },
            { time: '>=', name: $t('segments.timeOptions.since') },
            { time: '<=', name: $t('segments.timeOptions.before') },
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: '!withinLast', name: $t('segments.timeOptions.not within last') },
        ],
        survey: [
            { time: 'withinLast', name: $t('segments.timeOptions.within last') },
            { time: 'between', name: $t('segments.timeOptions.between') },
        ],
    };
}

export function getFactOptions ({ id, onlyAllowGuideCompatibleRules, showFrustrations = true, showAccounts = true }) {
    const FACT_OPTIONS = groupBy([...getGuideFriendlyRules(), ...getUsedTagRules()], 'type');
    let options;

    switch (id) {
        case 'application':
            options = FACT_OPTIONS.application;
            break;
        case 'page':
            options = FACT_OPTIONS.page;
            break;
        case 'feature':
            options = FACT_OPTIONS.feature;
            break;
        case 'trackType':
            options = FACT_OPTIONS.trackType;
            break;
        case 'resourceCenter':
            options = FACT_OPTIONS.guide.filter((option) => option.id !== 'visitor.lastviewed');
            break;
        case 'guide':
            options = FACT_OPTIONS.guide;
            break;
        case 'guideElement':
            options = FACT_OPTIONS.guideElement;
            break;
        case 'journey':
            options = FACT_OPTIONS.journey;
            break;
        case 'email':
            options = FACT_OPTIONS.email;
            break;
        case 'emailElement':
            options = FACT_OPTIONS.emailElement;
            break;
    }

    const includeParentAccountRules = hasSubscriptionFlag('usesSubaccounts');

    return options.filter((option) => {
        if (option.schema === 'aggregation' && onlyAllowGuideCompatibleRules) {
            return false;
        }

        if (option.id.includes('parentAccount') && !includeParentAccountRules) {
            return false;
        }
        if (!showFrustrations && FRUSTRATION_TYPES.some((frustrationType) => option.id.includes(frustrationType))) {
            return false;
        }
        if (!showAccounts && ['account', 'parentAccount'].some((kind) => option.id.includes(kind))) {
            return false;
        }

        return true;
    });
}

export function scrollIntoViewIfNeeded (node, boundary, { extra = 0 } = {}) {
    const [actions] = computeScrollIntoView(node, {
        scrollMode: 'if-needed',
        block: 'end',
        boundary,
    });

    if (actions) {
        const canSmoothScroll = 'scrollBehavior' in document.body.style;
        const { el, top } = actions;
        if (el.scroll && canSmoothScroll) {
            el.scroll({ top: top + extra, behavior: 'smooth' });
        } else {
            el.scrollTop = top + extra;
        }
    }
}

export function getAbsoluteDateRangeConfig (rule) {
    return {
        component: 'absolute-date-range',
        props: {
            value: {
                first: rule.first,
                last: rule.last,
            },
        },
    };
}

export function getRelativeDateRangeConfig (rule) {
    return {
        component: 'relative-date-range',
        props: {
            value: {
                granularity: rule.granularity,
                count: rule.count,
            },
        },
    };
}

export function getSingleDateConfig (rule, field = 'value') {
    return {
        component: 'single-date',
        props: {
            field,
            value: rule[field],
        },
    };
}

export function getCsvConfig (rule) {
    const props = {
        field: ['tagId', 'value'],
        kind: rule.kind,
        value: {
            tagId: rule.tagId,
            value: rule.value,
        },
    };

    if (rule.field && rule.kind === 'visitor' && rule.id !== 'visitor_auto_id') {
        props.metadataField = rule.field.split('.').slice(1).join('.');
    } else if (rule.kind === 'visitor' && rule.id === 'visitor_auto_id') {
        props.metadataField = 'visitorId';
    }

    return {
        component: 'csv-upload',
        props,
    };
}

export function getNumberConfig (rule, field = 'value', min = 0) {
    const numberConfig = {
        component: 'number-input',
        props: {
            field,
            value: rule[field],
            min,
        },
    };

    // TEMPORARY to unblock customers - follow up card https://pendo-io.atlassian.net/browse/APP-35003
    // will address this more formally with changes to the component layer
    if (rule.schema === 'float') {
        numberConfig.component = 'string-input';
    }

    return numberConfig;
}

export function getStringConfig (rule, field = 'value') {
    const schema = get(store.state.metadata.map, rule.field, { neverIndex: false, type: '' });
    const { neverIndex, Type } = schema;
    const supportedAutoFields = {
        id: true,
        lastservername: true,
        accountid: true,
    };

    const fieldName = rule.field?.split('.')[2];

    if (
        rule.schema !== 'string' ||
        Type !== 'string' ||
        (rule.operator !== '==' && rule.operator !== '!=') ||
        rule.useEventProperty ||
        ((neverIndex || rule.group === 'auto') && !supportedAutoFields[fieldName])
    ) {
        return {
            component: 'string-input',
            props: {
                field,
                value: rule[field],
            },
        };
    }

    return {
        component: 'metadata-value-select',
        props: {
            field,
            rule,
            value: rule[field],
        },
    };
}

export function formatFactOptions (options) {
    const ungroupedOptions = options.map((factOption) => {
        const [kind, fact] = factOption.id.split('.');
        const iconType = getOptionIconType({ ...factOption, kind });

        return {
            kind,
            ...factOption,
            fact,
            icon: {
                type: iconType,
                size: 14,
            },
        };
    });

    return groupOptionsByKind(ungroupedOptions);
}

export function getFactValue (rule, options) {
    const fields = ['fact', 'kind', 'schema'];

    return options
        .reduce((acc, option) => {
            return acc.concat(option.options);
        }, [])
        .find((option) => {
            return fields.every((field) => option[field] === rule[field]);
        });
}

export function getNoResourcesFoundText ({ rule, isMultiApp, resourceType }) {
    if (isMultiApp && rule.appId !== '*' && rule.id !== 'utm') {
        return {
            page: $t('segments.noResourcesFoundText.No pages found. Please select a different app.'),
            feature: $t('segments.noResourcesFoundText.No features found. Please select a different app.'),
            trackType: $t('segments.noResourcesFoundText.No track events found. Please select a different app.'),
            guide: $t('segments.noResourcesFoundText.No guides found. Please select a different app.'),
            guideExperiment: $t('segments.noResourcesFoundText.No experiments found. Please select a different app.'),
            resourceCenter: $t(
                'segments.noResourcesFoundText.No resource centers found. Please select a different app.',
            ),
            poll: $t('segments.noResourcesFoundText.No polls found. Please select a different app.'),
            journey: $t('segments.noResourcesFoundText.No journeys found. Please select a different app.'),
            email: $t('segments.noResourcesFoundText.No emails found. Please select a different app.'),
            survey: $t('segments.noResourcesFoundText.No surveys found. Please select a different app.'),
            utm: $t('segments.noResourcesFoundText.No session properties found. Please select a different app.'),
        }[resourceType || rule.id];
    }

    return {
        page: $t('segments.noResourcesFoundText.No pages found.'),
        feature: $t('segments.noResourcesFoundText.No features found.'),
        trackType: $t('segments.noResourcesFoundText.No track events found.'),
        guide: $t('segments.noResourcesFoundText.No guides found.'),
        guideExperiment: $t('segments.noResourcesFoundText.No experiments found.'),
        resourceCenter: $t('segments.noResourcesFoundText.No resource centers found.'),
        poll: $t('segments.noResourcesFoundText.No polls found.'),
        journey: $t('segments.noResourcesFoundText.No journeys found.'),
        email: $t('segments.noResourcesFoundText.No emails found.'),
        survey: $t('segments.noResourcesFoundText.No surveys found.'),
        utm: $t('segments.noResourcesFoundText.No session properties found.'),
    }[resourceType || rule.id];
}

export function isRuleValid (rule) {
    const validations = [];

    if (
        ['page', 'feature', 'trackType', 'guide', 'guideElement', 'emailElement', 'poll', 'survey'].includes(rule.id) &&
        !rule[`${rule.id}Id`]
    ) {
        return false;
    }

    if (rule.id === 'guide') {
        if (!rule.time || !rule.fact) {
            return false;
        }

        switch (rule.time) {
            case 'withinLast':
            case '!withinLast':
                validations.push('granularity', 'count');
                break;
            case 'atleast':
            case 'atmost':
                validations.push('value');
                break;
            case '>=':
            case '<=':
                validations.push('first');
                break;
            case 'between':
                validations.push('first', 'last');
                break;
        }
    }

    if (rule.id === 'application') {
        validations.push('appId', 'used');

        switch (rule.used) {
            case 'withinLast':
            case '!withinLast':
                validations.push('granularity', 'count');
                break;
            case 'since':
            case 'before':
                validations.push('value');
                break;
            case 'between':
                validations.push('first', 'last');
                break;
        }
    }

    if (rule.id === 'resourceCenter') {
        return !!rule.time && !!rule.fact;
    }

    if (rule.id === 'poll') {
        if (!rule.guideId) {
            return false;
        }

        switch (rule.time) {
            case 'withinLast':
            case '!withinLast':
            case 'withinNext':
            case '!withinNext':
                validations.push('granularity', 'count');
                break;
            case 'between':
                validations.push('first', 'last');
                break;
            case '>=':
            case '<=':
                validations.push('first');
                break;
        }

        if (!['responded', '!responded'].includes(rule.operator)) {
            validations.push('value');
        }
    }

    if (rule.id === 'feedbackRequest') {
        return !!rule.feedbackRequestId;
    }

    if (rule.id === 'survey') {
        validations.push('value');
    }

    if (rule.id === 'idea') {
        validations.push('ideaId', 'hasVoted');

        if (rule.hasVoted) {
            validations.push('importance');
        }

        if (rule.time === 'withinLast') {
            validations.push('granularity', 'count');
        } else if (rule.time === 'between') {
            validations.push('first', 'last');
        }
    }

    if (!rule.schema) {
        return false;
    }

    if (rule.schema === 'segment') {
        return !!rule.segmentId;
    }

    if (rule.schema === 'aggregation') {
        if (rule.useEventProperty && !rule.automaticEventProperty) {
            const opDetails = getOperatorOptions({ schema: rule.eventPropertySchema }).find(
                ({ operator }) => operator === rule.eventPropertyOperator,
            );

            if (
                !opDetails?.unary &&
                !isArray(rule.eventPropertyValue) &&
                String(rule.eventPropertyValue).length > EVENT_PROPERTY_VALUE_CHAR_LIMIT
            ) {
                return false;
            }

            if (isArray(rule.eventPropertyValue) && rule.eventPropertyValue.length === 0) {
                return false;
            }
        }

        validations.push('time');
        if (rule.time === 'withinLast') {
            validations.push('granularity', 'count');
        }

        if (rule.time === 'between') {
            validations.push('first', 'last');
        }
    }

    if (rule.schema === 'counter' && rule.id !== 'idea') {
        validations.push('used');
        if (rule.used === 'withinLast') {
            validations.push('granularity', 'count');
        }

        if (['since', 'atmost', 'atleast'].includes(rule.used)) {
            validations.push('value');
        }
    }

    if (rule.id === 'guideExperiment') {
        return !!rule.experimentId && !!rule.variantName;
    }

    if (rule.id === 'trackType') {
        if (rule.schema === 'aggregation' && rule.value === 0) {
            return false;
        }
    }

    switch (rule.operator) {
        case 'withinLast':
        case '!withinLast':
        case 'withinNext':
        case '!withinNext':
            validations.push('granularity', 'count');
            break;
        case 'between':
            validations.push('first', 'last');
            break;
        case '>=':
        case '<=':
            validations.push('value');
            break;
        case 'listContains':
        case '!listContains':
            validations.push('tagId', 'value');
            break;
    }

    return validations.every((validator) => rule[validator] != null);
}
